import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import {
  User, Trophy, Play, Star, GraduationCap, Settings, LogOut, 
  ExternalLink, Edit3, Save, X, Plus, Trash2, Upload, Copy,
  CheckCircle, School, Ruler, ArrowLeft, Share2, Link2,
  ChevronDown, ChevronUp, Calendar, Eye, Sun, Moon, Mail, Inbox, Clock, Search, Twitter, DollarSign, Info, Phone, Loader2, Users
} from 'lucide-react';
import { API, POSITION_OPTIONS } from '../lib/constants';
import { ActionProgress } from '../components/ActionProgress';

// Label with a hover tooltip hint (why it matters to recruiters)
const LabelHint = ({ children, hint, darkMode }) => (
  <div className="flex items-center gap-1.5 mb-1">
    <Label className="mb-0">{children}</Label>
    {hint && (
      <span className="group relative inline-flex" tabIndex={0}>
        <Info className={`h-3.5 w-3.5 cursor-help ${darkMode ? 'text-white/30' : 'text-gray-400'}`} />
        <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-50 w-48 rounded-lg bg-slate-900 text-white text-[11px] leading-snug px-2.5 py-1.5 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity shadow-lg">
          {hint}
        </span>
      </span>
    )}
  </div>
);

// Position Selector
const PositionSelector = ({ selected = [], onChange }) => {
  const categories = [
    { key: 'offense', label: 'OFF', positions: POSITION_OPTIONS.offense },
    { key: 'defense', label: 'DEF', positions: POSITION_OPTIONS.defense },
    { key: 'specialTeams', label: 'ST', positions: POSITION_OPTIONS.specialTeams },
  ];
  const toggle = (pos) => onChange(selected.includes(pos) ? selected.filter(p => p !== pos) : [...selected, pos]);
  return (
    <div className="space-y-2">
      {categories.map(cat => (
        <div key={cat.key}>
          <p className="text-xs text-gray-400 mb-1 uppercase tracking-wider">{cat.label}</p>
          <div className="flex flex-wrap gap-1">
            {cat.positions.map(pos => (
              <button key={pos} type="button" onClick={() => toggle(pos)}
                className={`px-2.5 py-1 rounded-full text-xs font-bold transition-all ${
                  selected.includes(pos) ? 'bg-[#0F5132] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>{pos}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// Verify Sender Button
const VerifySenderButton = ({ messageId, userId, darkMode, onVerificationSent }) => {
  const [loading, setLoading] = useState(false);
  
  const handleVerify = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/dashboard/${userId}/messages/${messageId}/send-verification`);
      if (res.data.success) {
        onVerificationSent(res.data.verification_token);
        toast.success('Verification link generated!');
      }
    } catch (err) {
      toast.error('Failed to generate verification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button onClick={handleVerify} disabled={loading}
      className="text-[#16A34A] hover:text-[#16A34A] text-xs font-semibold flex items-center gap-1 disabled:opacity-50">
      {loading ? <Clock className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3" />}
      {loading ? 'Generating...' : 'Verify Sender'}
    </button>
  );
};

// Copy Verification Link
const CopyVerifyLink = ({ token, darkMode }) => {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/verify/${token}`;

  const copy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Verification link copied! Send it to the message sender.');
    setTimeout(() => setCopied(false), 3000);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button onClick={copy}
        className={`text-xs font-semibold flex items-center gap-1 ${copied ? 'text-green-500' : 'text-[#16A34A] hover:text-[#16A34A]'}`}>
        {copied ? <><CheckCircle className="h-3 w-3" /> Link Copied!</> : <><Copy className="h-3 w-3" /> Copy Verification Link</>}
      </button>
      <span className={`text-[10px] ${darkMode ? 'text-white/20' : 'text-gray-300'}`}>
        Send this link to the sender to verify their identity
      </span>
    </div>
  );
};

const VerifyBanner = ({ email, onVerified }) => {
  const [sent, setSent] = useState(false);
  const [channel, setChannel] = useState(null); // 'email' | 'twitter'
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const requestEmail = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/auth/request-code`, { email }, { withCredentials: true });
      setChannel('email'); setSent(true);
      toast.success(`6-digit code sent to ${email}`);
    } catch { toast.error('Could not send code. Try again.'); }
    finally { setBusy(false); }
  };

  const requestTwitter = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/auth/request-twitter-verification`, {}, { withCredentials: true });
      setChannel('twitter'); setSent(true);
      toast.success("We've asked an admin to DM your code on X.");
    } catch { toast.error('Could not request X verification.'); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (!/^\d{6}$/.test(code)) { toast.error('Enter the 6-digit code'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/api/auth/verify-code`, { email, code }, { withCredentials: true });
      if (res.data.success) { toast.success("You're verified — your profile is now live!"); onVerified(); }
    } catch { toast.error('That code is invalid or expired. Request a new one.'); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="verify-banner" className="bg-amber-400 text-black">
      <div className="max-w-4xl mx-auto px-4 py-3">
        <div className="flex items-start sm:items-center gap-3 flex-col sm:flex-row sm:justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Clock className="h-5 w-5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-bold">Your profile is private until you verify your account.</p>
              <p className="text-xs text-black/70">
                {sent
                  ? (channel === 'twitter'
                      ? 'An admin will DM your 6-digit code on X. Enter it below once you get it.'
                      : `Enter the 6-digit code we emailed to ${email}.`)
                  : 'Verify with a 6-digit code to go live.'}
              </p>
            </div>
          </div>

          {!sent ? (
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button size="sm" onClick={requestEmail} disabled={busy} data-testid="verify-email-btn"
                className="bg-black text-amber-400 hover:bg-zinc-800 font-bold rounded-full text-xs">
                {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Mail className="h-3 w-3 mr-1" />}
                Email me a 6-digit code
              </Button>
              <Button size="sm" onClick={requestTwitter} disabled={busy} data-testid="verify-twitter-btn"
                className="bg-black text-amber-400 hover:bg-zinc-800 font-bold rounded-full text-xs">
                <Twitter className="h-3 w-3 mr-1" /> Twitter DM
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-shrink-0">
              <Input
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                placeholder="123456"
                data-testid="verify-code-input"
                onKeyPress={e => e.key === 'Enter' && verify()}
                className="h-9 w-28 bg-white border-black/20 text-black text-center text-lg tracking-[0.3em] font-bold rounded-lg"
              />
              <Button size="sm" onClick={verify} disabled={code.length !== 6 || busy} data-testid="verify-code-submit-btn"
                className="bg-black text-amber-400 hover:bg-zinc-800 font-bold rounded-full text-xs">
                {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                Verify
              </Button>
              <button onClick={channel === 'twitter' ? requestTwitter : requestEmail} disabled={busy}
                data-testid="verify-resend-btn" className="text-black/60 hover:text-black text-xs underline">
                Resend
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const realUserId = localStorage.getItem('userId');
  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  // Admin read-only impersonation ("Preview dashboard")
  const impersonateId = (isAdmin && sessionStorage.getItem('impersonateUserId')) || null;
  const impersonateName = sessionStorage.getItem('impersonateName') || '';
  const isImpersonating = !!impersonateId;
  // Admin editable session for an NFL Legend (full dashboard, writes allowed)
  const legendEditId = (isAdmin && !impersonateId && sessionStorage.getItem('legendEditUserId')) || null;
  const legendEditName = sessionStorage.getItem('legendEditName') || '';
  const isLegendEdit = !!legendEditId;
  const userId = impersonateId || legendEditId || realUserId;
  const storedUsername = localStorage.getItem('username');

  const exitImpersonation = () => {
    sessionStorage.removeItem('impersonateUserId');
    sessionStorage.removeItem('impersonateName');
    sessionStorage.removeItem('legendEditUserId');
    sessionStorage.removeItem('legendEditName');
    navigate('/admin');
  };
  
  // Core state
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('feed');
  const [isVerified, setIsVerified] = useState(true);
  const [userEmail, setUserEmail] = useState(localStorage.getItem('userEmail') || '');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('dashboardTheme') === 'dark');
  
  const toggleTheme = () => {
    setDarkMode(prev => {
      const next = !prev;
      localStorage.setItem('dashboardTheme', next ? 'dark' : 'light');
      return next;
    });
  };
  
  // Edit states
  const [editingProfile, setEditingProfile] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [photos, setPhotos] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [action, setAction] = useState(null); // {label, progress|null}
  const startAction = (label) => setAction({ label, progress: null });
  const trackUpload = (e) => { if (e.total) setAction(a => a ? { ...a, progress: Math.round((e.loaded * 100) / e.total) } : a); };
  const endAction = () => setAction(null);
  const [coachForm, setCoachForm] = useState({ name: '', email: '', phone: '' });
  
  // Data states
  const [offers, setOffers] = useState([]);
  const [videos, setVideos] = useState([]);
  const [availableVideos, setAvailableVideos] = useState([]);
  const [stats, setStats] = useState([]);
  const [interests, setInterests] = useState([]);
  const [camps, setCamps] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [measurables, setMeasurables] = useState({});
  const [messages, setMessages] = useState([]);
  const [feed, setFeed] = useState([]);
  const [posts, setPosts] = useState([]);
  const [inviteData, setInviteData] = useState(null);
  const [newPost, setNewPost] = useState('');
  const [shareToTeam, setShareToTeam] = useState(false);
  const [postType, setPostType] = useState('update');
  const [posting, setPosting] = useState(false);
  
  // Offer form
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [offerForm, setOfferForm] = useState({ collegeId: '', status: 'offer', note: '', sourceUrl: '', tweet_url: '' });
  const [editingOffer, setEditingOffer] = useState(null);
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventForm, setEventForm] = useState({ camp_name: '', camp_date: '', location: '', status: 'registered', notes: '', share_to_team: false });
  const [editingEvent, setEditingEvent] = useState(null);
  const [collegeSearch, setCollegeSearch] = useState('');
  
  // Video form
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [videoForm, setVideoForm] = useState({ video_title: '', hudl_video_url: '', video_description: '' });
  
  // Measurables edit
  const [editingMeasurables, setEditingMeasurables] = useState(false);
  const [measurablesForm, setMeasurablesForm] = useState({});

  // Stats
  const [showAddSeason, setShowAddSeason] = useState(false);
  const [newSeasonName, setNewSeasonName] = useState('');
  const [selectedSeason, setSelectedSeason] = useState('');
  const [editingStats, setEditingStats] = useState(false);
  const [currentStats, setCurrentStats] = useState({ season: '', games_played: 0, offensive_stats: {}, defensive_stats: {}, special_teams_stats: {} });

  // Redirect if not logged in
  useEffect(() => {
    if (!userId) { navigate('/signin'); return; }
    loadDashboard();
    loadColleges();
  }, [userId]);

  // Background HUDL import can populate videos minutes after the dashboard loads —
  // poll a few times so highlights appear automatically without a manual refresh.
  const [videoPollTick, setVideoPollTick] = useState(0);
  const videoPollRef = useRef({ tries: 0 });
  useEffect(() => {
    if (loading || !userId) return;
    if (videos.length > 0 || availableVideos.length > 0) return;
    if (videoPollRef.current.tries >= 6) return;
    const timer = setTimeout(async () => {
      videoPollRef.current.tries += 1;
      try {
        const [vR, aR] = await Promise.all([
          axios.get(`${API}/api/dashboard/${userId}/videos`).catch(() => ({ data: { videos: [] } })),
          axios.get(`${API}/api/dashboard/${userId}/videos/available`).catch(() => ({ data: { videos: [] } })),
        ]);
        const gotSelected = (vR.data.videos || []).length;
        const gotAvailable = (aR.data.videos || []).length;
        if (gotSelected) setVideos(vR.data.videos);
        if (gotAvailable) setAvailableVideos(aR.data.videos);
        // Nothing yet and attempts remain → reschedule the next poll.
        if (!gotSelected && !gotAvailable && videoPollRef.current.tries < 6) {
          setVideoPollTick(t => t + 1);
        }
      } catch {}
    }, 10000);
    return () => clearTimeout(timer);
  }, [loading, videos.length, availableVideos.length, userId, videoPollTick]);

  // Read-only enforcement while an admin previews a user's dashboard:
  // block every mutating API call (PUT/POST/DELETE) at the network layer.
  useEffect(() => {
    if (!isImpersonating) return;
    const id = axios.interceptors.request.use((config) => {
      const method = (config.method || 'get').toLowerCase();
      const url = config.url || '';
      const isMutation = ['put', 'post', 'delete', 'patch'].includes(method);
      // allow read-style POSTs needed just to render the dashboard
      const readWhitelist = url.includes('/generate-invite') || url.includes('/view');
      if (isMutation && !readWhitelist) {
        toast.info('Read-only preview — changes are disabled');
        return Promise.reject(new axios.Cancel('read-only-preview'));
      }
      return config;
    });
    return () => axios.interceptors.request.eject(id);
  }, [isImpersonating]);

  const loadDashboard = async () => {
    try {
      const [dashRes, offersRes, videosRes, availRes, statsRes, interestsRes, campsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/api/dashboard/${userId}`),
        axios.get(`${API}/api/dashboard/${userId}/offers`).catch(() => ({ data: { offers: [] } })),
        axios.get(`${API}/api/dashboard/${userId}/videos`).catch(() => ({ data: { videos: [] } })),
        axios.get(`${API}/api/dashboard/${userId}/videos/available`).catch(() => ({ data: { videos: [] } })),
        axios.get(`${API}/api/dashboard/${userId}/comprehensive-stats`).catch(() => ({ data: { stats: [] } })),
        axios.get(`${API}/api/dashboard/${userId}/school-interests`).catch(() => ({ data: { interests: [] } })),
        axios.get(`${API}/api/dashboard/${userId}/camps`).catch(() => ({ data: { camps: [] } })),
        axios.get(`${API}/api/dashboard/${userId}/messages`).catch(() => ({ data: { messages: [] } })),
      ]);

      const profile = dashRes.data.profile || dashRes.data;
      setProfileData(profile);
      setEditForm({ ...profile });
      const u = dashRes.data.user || {};
      setIsVerified(!!u.is_verified);
      if (u.email) setUserEmail(u.email);
      axios.get(`${API}/api/dashboard/${userId}/photos`).then(r => setPhotos(r.data.photos || [])).catch(() => {});
      setMeasurables(dashRes.data.measurables || {});
      setMeasurablesForm(dashRes.data.measurables || {});
      setOffers(offersRes.data.offers || []);
      setVideos(videosRes.data.videos || []);
      setAvailableVideos(availRes.data.videos || []);
      setStats(statsRes.data.stats || []);
      setInterests(interestsRes.data.interests || []);
      setCamps(campsRes.data.camps || []);
      setMessages(messagesRes.data.messages || []);
      
      // Load feed and invite separately (non-blocking)
      try {
        const [feedRes, inviteRes, coachRes] = await Promise.all([
          axios.get(`${API}/api/feed/${userId}`).catch(() => ({ data: { events: [] } })),
          axios.post(`${API}/api/team/generate-invite?user_id=${userId}`).catch(() => ({ data: null })),
          axios.get(`${API}/api/dashboard/${userId}/coach`).catch(() => ({ data: { coach: {} } })),
        ]);
        setFeed(feedRes.data.events || []);
        if (inviteRes.data?.invite) setInviteData(inviteRes.data.invite);
        const c = coachRes.data?.coach || {};
        setCoachForm({ name: c.name || '', email: c.email || '', phone: c.phone || '' });
      } catch {}
    } catch (err) {
      console.error('Dashboard load error:', err);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadColleges = async () => {
    try {
      const res = await axios.get(`${API}/api/colleges`);
      setColleges(res.data.colleges || res.data || []);
    } catch {}
  };

  // Profile update
  const saveProfile = async () => {
    try {
      await axios.put(`${API}/api/profile/${userId}`, editForm);
      setProfileData(editForm);
      // Save head coach info if provided (requires name + email)
      if (coachForm.name?.trim()) {
        if (!coachForm.email?.trim()) {
          toast.warning('Profile saved. Add the coach email to save Head Coach info.');
          setEditingProfile(false);
          return;
        }
        await axios.put(`${API}/api/dashboard/${userId}/coach`, {
          name: coachForm.name.trim(), email: coachForm.email.trim(), phone: coachForm.phone?.trim() || null,
        }).catch(() => {});
      }
      setEditingProfile(false);
      toast.success('Profile updated!');
    } catch (err) {
      toast.error('Failed to update profile');
    }
  };

  // Photos & custom links
  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoUploading(true);
    startAction('Uploading photo');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/dashboard/${userId}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: trackUpload });
      setPhotos(prev => [...prev, res.data.photo]);
      toast.success('Photo added!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to upload photo'); }
    setPhotoUploading(false);
    endAction();
    e.target.value = '';
  };
  const deletePhoto = async (photoId) => {
    try {
      await axios.delete(`${API}/api/dashboard/${userId}/photos/${photoId}`);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success('Photo removed');
    } catch (err) { toast.error('Failed to remove photo'); }
  };
  const addCustomLink = () => setEditForm(p => ({ ...p, custom_links: [...(p.custom_links || []), { id: Date.now().toString(), label: '', url: '' }] }));
  const updateCustomLink = (id, key, val) => setEditForm(p => ({ ...p, custom_links: (p.custom_links || []).map(l => l.id === id ? { ...l, [key]: val } : l) }));
  const removeCustomLink = (id) => setEditForm(p => ({ ...p, custom_links: (p.custom_links || []).filter(l => l.id !== id) }));

  // Measurables
  const saveMeasurables = async () => {
    try {
      await axios.put(`${API}/api/measurables/${userId}`, measurablesForm);
      setMeasurables(measurablesForm);
      setEditingMeasurables(false);
      toast.success('Measurables updated!');
    } catch (err) {
      toast.error('Failed to update measurables');
    }
  };

  // Offers
  const saveEvent = async () => {
    if (!eventForm.camp_name.trim()) { toast.error('Event name is required'); return; }
    try {
      if (editingEvent) {
        await axios.put(`${API}/api/dashboard/${userId}/camps/${editingEvent}`, eventForm);
      } else {
        await axios.post(`${API}/api/dashboard/${userId}/camps`, eventForm);
      }
      const r = await axios.get(`${API}/api/dashboard/${userId}/camps`).catch(() => ({ data: { camps: [] } }));
      setCamps(r.data.camps || []);
      setShowEventForm(false);
      setEditingEvent(null);
      setEventForm({ camp_name: '', camp_date: '', location: '', status: 'registered', notes: '', share_to_team: false });
      toast.success(editingEvent ? 'Event updated!' : 'Event added!');
    } catch (err) {
      toast.error(editingEvent ? 'Failed to update event' : 'Failed to add event');
    }
  };

  const openEditEvent = (c) => {
    setEventForm({
      camp_name: c.camp_name || '',
      camp_date: c.camp_date || '',
      location: c.location || '',
      status: c.status || 'registered',
      notes: c.notes || '',
      share_to_team: c.share_to_team || false,
    });
    setEditingEvent(c.id);
    setShowEventForm(true);
  };

  const deleteEvent = async (id) => {
    try {
      await axios.delete(`${API}/api/dashboard/${userId}/camps/${id}`);
      setCamps(prev => prev.filter(c => c.id !== id));
      toast.success('Event removed');
    } catch (err) {
      toast.error('Failed to remove event');
    }
  };

  const addOffer = async () => {
    try {
      await axios.post(`${API}/api/dashboard/${userId}/offers`, offerForm);
      const res = await axios.get(`${API}/api/dashboard/${userId}/offers`);
      setOffers(res.data.offers || []);
      setShowOfferForm(false);
      setOfferForm({ collegeId: '', status: 'offer', note: '', sourceUrl: '', tweet_url: '' });
      setCollegeSearch('');
      toast.success('Offer added!');
    } catch (err) {
      toast.error('Failed to add offer');
    }
  };

  const updateOffer = async () => {
    if (!editingOffer) return;
    try {
      await axios.put(`${API}/api/dashboard/${userId}/offers/${editingOffer.id}`, offerForm);
      const res = await axios.get(`${API}/api/dashboard/${userId}/offers`);
      setOffers(res.data.offers || []);
      setEditingOffer(null);
      setOfferForm({ collegeId: '', status: 'offer', note: '', sourceUrl: '', tweet_url: '' });
      toast.success('Offer updated!');
    } catch (err) {
      toast.error('Failed to update offer');
    }
  };

  const deleteOffer = async (offerId) => {
    try {
      await axios.delete(`${API}/api/dashboard/${userId}/offers/${offerId}`);
      setOffers(prev => prev.filter(o => o.id !== offerId));
      toast.success('Offer removed');
    } catch (err) {
      toast.error('Failed to remove offer');
    }
  };

  // Videos
  const addVideo = async () => {
    startAction('Adding your highlight');
    try {
      await axios.post(`${API}/api/dashboard/${userId}/video`, videoForm);
      const res = await axios.get(`${API}/api/dashboard/${userId}/videos`);
      setVideos(res.data.videos || []);
      setShowVideoForm(false);
      setVideoForm({ video_title: '', hudl_video_url: '', video_description: '' });
      toast.success('Video added!');
    } catch (err) {
      toast.error('Failed to add video');
    } finally {
      endAction();
    }
  };

  const deleteVideo = async (videoId) => {
    try {
      await axios.delete(`${API}/api/dashboard/${userId}/video/${videoId}`);
      setVideos(prev => prev.filter(v => v.id !== videoId));
      toast.success('Video removed');
    } catch (err) {
      toast.error('Failed to remove video');
    }
  };

  const addFromAvailable = async (videoId) => {
    startAction('Adding highlight to your profile');
    try {
      await axios.post(`${API}/api/dashboard/${userId}/videos/add-from-available`, { video_id: videoId });
      const [videosRes, availRes] = await Promise.all([
        axios.get(`${API}/api/dashboard/${userId}/videos`),
        axios.get(`${API}/api/dashboard/${userId}/videos/available`),
      ]);
      setVideos(videosRes.data.videos || []);
      setAvailableVideos(availRes.data.videos || []);
      toast.success('Video added to profile!');
    } catch (err) {
      toast.error('Failed to add video');
    } finally {
      endAction();
    }
  };

  // Profile picture upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    startAction('Updating profile photo');
    try {
      const res = await axios.post(`${API}/api/profile/${userId}/upload-picture`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: trackUpload
      });
      const newUrl = res.data.profile_picture_url || res.data.url;
      if (newUrl) {
        setProfileData(prev => ({ ...prev, profile_picture_url: newUrl }));
        setEditForm(prev => ({ ...prev, profile_picture_url: newUrl }));
        toast.success('Photo updated!');
      }
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      endAction();
      e.target.value = '';
    }
  };

  // Stats
  const addSeason = async () => {
    if (!newSeasonName) return;
    const newSeason = { season: newSeasonName, games_played: 0, offensive_stats: {}, defensive_stats: {}, special_teams_stats: {} };
    try {
      await axios.post(`${API}/api/dashboard/${userId}/comprehensive-stats`, newSeason);
      const res = await axios.get(`${API}/api/dashboard/${userId}/comprehensive-stats`);
      setStats(res.data.stats || []);
      setSelectedSeason(newSeasonName);
      setCurrentStats(newSeason);
      setShowAddSeason(false);
      setNewSeasonName('');
      toast.success('Season added!');
    } catch (err) {
      toast.error('Failed to add season');
    }
  };

  const saveStats = async () => {
    try {
      await axios.post(`${API}/api/dashboard/${userId}/comprehensive-stats`, currentStats);
      const res = await axios.get(`${API}/api/dashboard/${userId}/comprehensive-stats`);
      setStats(res.data.stats || []);
      setEditingStats(false);
      toast.success('Stats saved!');
    } catch (err) {
      toast.error('Failed to save stats');
    }
  };

  const handleStatChange = (section, field, value) => {
    if (section === 'root') {
      setCurrentStats(prev => ({ ...prev, [field]: value }));
    } else {
      setCurrentStats(prev => ({
        ...prev,
        [section]: { ...(prev[section] || {}), [field]: value }
      }));
    }
  };

  // Copy link
  const copyLink = () => {
    const url = `${window.location.origin}/api/share/${profileData?.username || storedUsername}`;
    navigator.clipboard.writeText(url);
    toast.success('Share link copied!');
  };

  // Sign out
  const signOut = () => {
    axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    localStorage.clear();
    navigate('/');
  };

  // Load season when selected
  useEffect(() => {
    if (selectedSeason && stats.length > 0) {
      const season = stats.find(s => s.season === selectedSeason);
      if (season) setCurrentStats(season);
    }
  }, [selectedSeason, stats]);

  // Filter colleges for search
  const filteredColleges = colleges.filter(c => 
    c.name?.toLowerCase().includes(collegeSearch.toLowerCase())
  ).slice(0, 10);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="w-10 h-10 border-2 border-[#16A34A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Message functions
  const markMessageRead = async (messageId) => {
    try {
      await axios.put(`${API}/api/dashboard/${userId}/messages/${messageId}/read`);
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, read: true } : m));
    } catch {}
  };

  const deleteMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/api/dashboard/${userId}/messages/${messageId}`);
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Message deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const unreadCount = messages.filter(m => !m.read).length;

  // Post functions
  const createPost = async () => {
    if (!newPost.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/api/dashboard/${userId}/posts`, { content: newPost, post_type: postType, share_to_team: shareToTeam });
      setNewPost('');
      setShareToTeam(false);
      // Refresh feed
      const feedRes = await axios.get(`${API}/api/feed/${userId}`).catch(() => ({ data: { events: [] } }));
      setFeed(feedRes.data.events || []);
      toast.success('Posted!');
    } catch (err) {
      toast.error('Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const reactToPost = async (postId, reaction) => {
    try {
      await axios.post(`${API}/api/posts/${postId}/react?user_id=${userId}&reaction=${reaction}`);
      // Refresh feed
      const feedRes = await axios.get(`${API}/api/feed/${userId}`).catch(() => ({ data: { events: [] } }));
      setFeed(feedRes.data.events || []);
    } catch {}
  };

  // Also add Home, Users icons
  const tabs = [
    { id: 'feed', icon: <Inbox className="h-4 w-4" />, label: 'Home' },
    { id: 'profile', icon: <User className="h-4 w-4" />, label: 'Profile' },
    { id: 'inbox', icon: <Mail className="h-4 w-4" />, label: 'Inbox' },
    { id: 'offers', icon: <Trophy className="h-4 w-4" />, label: 'Offers' },
    { id: 'events', icon: <Calendar className="h-4 w-4" />, label: 'Events' },
    { id: 'highlights', icon: <Play className="h-4 w-4" />, label: 'Highlights' },
    { id: 'stats', icon: <Star className="h-4 w-4" />, label: 'Stats' },
    { id: 'measurables', icon: <Ruler className="h-4 w-4" />, label: 'Measurables' },
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-black text-white dashboard-dark' : 'bg-gray-50 text-gray-900'}`}>
      <ActionProgress active={!!action} progress={action?.progress} label={action?.label} />
      {/* Legend edit (editable) banner */}
      {isLegendEdit && (
        <div className="bg-[#16A34A] text-white px-4 py-2 flex items-center justify-between gap-3 sticky top-0 z-[60]" data-testid="legend-edit-banner">
          <div className="flex items-center gap-2 text-sm font-bold min-w-0">
            <Trophy className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">Editing {legendEditName || 'this player'}'s profile — changes save live</span>
          </div>
          <button onClick={exitImpersonation} data-testid="exit-legend-edit-btn"
            className="bg-white text-[#16A34A] text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1.5 flex-shrink-0 hover:bg-white/90">
            <X className="h-3.5 w-3.5" /> Done
          </button>
        </div>
      )}
      {/* Impersonation (read-only preview) banner */}
      {isImpersonating && (
        <div className="bg-amber-400 text-black px-4 py-2 flex items-center justify-between gap-3 sticky top-0 z-[60]" data-testid="impersonation-banner">
          <div className="flex items-center gap-2 text-sm font-bold min-w-0">
            <Eye className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">Previewing {impersonateName || 'this player'}'s dashboard — read-only</span>
          </div>
          <button onClick={exitImpersonation} data-testid="exit-impersonation-btn"
            className="bg-black text-amber-400 text-xs font-bold px-4 py-1.5 rounded-full flex items-center gap-1.5 flex-shrink-0 hover:bg-zinc-800">
            <X className="h-3.5 w-3.5" /> Exit preview
          </button>
        </div>
      )}
      {/* Top Bar */}
      <div className={`border-b sticky ${(isImpersonating || isLegendEdit) ? 'top-10' : 'top-0'} z-50 transition-colors duration-300 ${darkMode ? 'bg-zinc-950/90 backdrop-blur-md border-white/10' : 'bg-white border-gray-200'}`}>
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <img src="/offersz-o.png" alt="Offersz" className="w-8 h-8 rounded-full flex-shrink-0" />
            <span className="font-black text-lg truncate">Dashboard</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <button onClick={toggleTheme}
              className={`flex items-center justify-center h-10 w-10 rounded-full transition-all flex-shrink-0 ${darkMode ? 'bg-white/10 text-[#16A34A] hover:bg-white/20' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
              title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <button onClick={copyLink} title="Copy link"
              className={`flex items-center justify-center h-10 w-10 sm:w-auto sm:px-3 gap-1 rounded-full border text-xs font-semibold flex-shrink-0 ${darkMode ? 'border-white/10 text-white/70 hover:bg-white/10' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
              <Copy className="h-4 w-4 sm:h-3 sm:w-3" /><span className="hidden sm:inline">Copy Link</span>
            </button>
            <button onClick={() => navigate('/directory')} data-testid="dashboard-discover"
              className={`hidden sm:flex items-center gap-1 h-10 px-3 rounded-full border text-xs font-semibold ${darkMode ? 'border-white/10 text-white/70 hover:bg-white/10' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
              <Search className="h-3 w-3" /> Discover
            </button>
            {isAdmin && (
              <button onClick={() => navigate('/admin')} data-testid="dashboard-admin-ads-btn"
                className={`hidden sm:flex items-center gap-1 h-10 px-3 rounded-full border text-xs font-semibold ${darkMode ? 'border-[#16A34A]/30 text-[#16A34A] hover:bg-[#16A34A]/10' : 'border-[#16A34A] text-[#1a1a1a] bg-[#16A34A]/10 hover:bg-[#16A34A]/20'}`}>
                <DollarSign className="h-3 w-3" /> Admin
              </button>
            )}
            <button onClick={() => navigate(`/profile/${profileData?.username || storedUsername}`)} data-testid="dashboard-view-profile"
              className="flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#16A34A] hover:bg-[#15803d] text-white text-sm font-bold flex-shrink-0">
              <Eye className="h-4 w-4" /> View Profile
            </button>
            <button onClick={signOut} title="Sign out" className={`flex items-center justify-center h-10 w-10 rounded-full flex-shrink-0 ${darkMode ? 'text-white/30 hover:text-white/60 hover:bg-white/5' : 'text-gray-400 hover:bg-gray-100'}`}>
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Account verification banner (own account only) */}
      {!isVerified && !isImpersonating && !isLegendEdit && (
        <VerifyBanner email={userEmail} onVerified={() => { setIsVerified(true); loadDashboard(); }} />
      )}

      {/* Tab Bar */}
      <div className={`border-b sticky top-14 z-40 transition-colors duration-300 ${darkMode ? 'bg-zinc-950/90 backdrop-blur-md border-white/10' : 'bg-white border-gray-200'}`}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex overflow-x-auto scrollbar-hide -mb-px">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'border-[#16A34A] text-[#16A34A]' 
                    : darkMode 
                      ? 'border-transparent text-white/30 hover:text-white/50'
                      : 'border-transparent text-gray-400 hover:text-gray-600'
                }`}>
                {tab.icon}
                {tab.label}
                {tab.id === 'inbox' && unreadCount > 0 && (
                  <Badge className="border-0 text-xs ml-1 bg-red-500 text-white">{unreadCount}</Badge>
                )}
                {tab.id === 'offers' && offers.length > 0 && (
                  <Badge className={`border-0 text-xs ml-1 ${darkMode ? 'bg-[#16A34A]/20 text-[#16A34A]' : 'bg-[#16A34A]/15 text-[#1a1a1a]'}`}>{offers.length}</Badge>
                )}
                {tab.id === 'highlights' && videos.length > 0 && (
                  <Badge className={`border-0 text-xs ml-1 ${darkMode ? 'bg-white/10 text-white/50' : 'bg-gray-100 text-gray-600'}`}>{videos.length}</Badge>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">

        {/* ──── FEED TAB ──── */}
        {activeTab === 'feed' && (
          <div className="space-y-4">
            {/* Invite Banner */}
            {inviteData && (
              <div className={`flex items-center gap-3 rounded-xl px-4 py-3 ${darkMode ? 'bg-[#16A34A]/10 border border-[#16A34A]/20' : 'bg-[#16A34A]/10 border border-[#16A34A]/30'}`}>
                <Share2 className="h-5 w-5 text-[#16A34A] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-bold ${darkMode ? 'text-[#16A34A]' : 'text-[#1a1a1a]'}`}>Invite your teammates</p>
                  <p className={`text-[10px] ${darkMode ? 'text-[#16A34A]/50' : 'text-[#16A34A]/60'}`}>{inviteData.uses > 0 ? `${inviteData.uses} joined` : 'Share your link'}</p>
                </div>
                <Button size="sm" onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/create-profile?invite=${inviteData.invite_code}`);
                  toast.success('Invite link copied!');
                }} className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold rounded-full text-xs px-3 h-8">
                  <Copy className="h-3 w-3 mr-1" /> Copy Link
                </Button>
              </div>
            )}

            {/* Post Composer */}
            <Card>
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center overflow-hidden ${darkMode ? 'bg-white/10' : 'bg-gray-100'}`}>
                    {profileData?.profile_picture_url ? (
                      <img src={profileData.profile_picture_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="font-bold text-sm">{profileData?.name?.charAt(0) || '?'}</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <textarea value={newPost} onChange={e => setNewPost(e.target.value)}
                      placeholder="What's happening? Share an update, workout, or achievement..."
                      rows={2}
                      className={`w-full resize-none text-sm rounded-xl px-3 py-2.5 border focus:outline-none focus:ring-2 focus:ring-[#16A34A]/30 ${
                        darkMode ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'
                      }`} />
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex gap-1 flex-wrap items-center">
                        {[
                          { type: 'update', label: '📝 Update' },
                          { type: 'workout', label: '💪 Workout' },
                          { type: 'achievement', label: '🏆 Achievement' },
                        ].map(pt => (
                          <button key={pt.type} onClick={() => setPostType(pt.type)}
                            className={`text-xs px-2 py-1 rounded-full transition-all ${
                              postType === pt.type
                                ? 'bg-[#0F5132] text-white font-bold'
                                : darkMode ? 'bg-white/5 text-white/40' : 'bg-gray-100 text-gray-500'
                            }`}>{pt.label}</button>
                        ))}
                        {profileData?.team_id && (
                          <button onClick={() => setShareToTeam(v => !v)} data-testid="post-share-team-toggle"
                            title="Also show this on your team page"
                            className={`text-xs px-2 py-1 rounded-full transition-all flex items-center gap-1 ${
                              shareToTeam ? 'bg-[#16A34A] text-white font-bold' : darkMode ? 'bg-white/5 text-white/40' : 'bg-gray-100 text-gray-500'
                            }`}>
                            <Users className="h-3 w-3" /> Share to team
                          </button>
                        )}
                      </div>
                      <Button onClick={createPost} disabled={!newPost.trim() || posting} size="sm"
                        className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold rounded-full px-4">
                        {posting ? 'Posting...' : 'Post'}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Feed Items */}
            {feed.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Inbox className={`h-12 w-12 mx-auto mb-3 ${darkMode ? 'text-white/10' : 'text-gray-300'}`} />
                  <p className={darkMode ? 'text-white/40' : 'text-gray-500'}>Your feed is empty</p>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-white/20' : 'text-gray-400'}`}>
                    Follow other players to see their updates, or post your own!
                  </p>
                  <Button onClick={() => navigate('/directory')} variant="outline" size="sm" className="mt-3 rounded-full">
                    Discover Players
                  </Button>
                </CardContent>
              </Card>
            ) : (
              feed.map(event => (
                <Card key={event.id}>
                  <CardContent className="p-4">
                    <div className="flex gap-3">
                      {/* Avatar */}
                      <button onClick={() => event.athlete?.username && navigate(`/profile/${event.athlete.username}`)}
                        className="w-10 h-10 rounded-full flex-shrink-0 overflow-hidden bg-gradient-to-br from-[#16A34A] to-green-400 flex items-center justify-center">
                        {event.athlete?.profile_picture_url ? (
                          <img src={event.athlete.profile_picture_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-black font-bold text-xs">{event.athlete?.name?.charAt(0) || '?'}</span>
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        {/* Header */}
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <button onClick={() => event.athlete?.username && navigate(`/profile/${event.athlete.username}`)}
                            className="font-bold text-sm hover:underline">{event.athlete?.name || 'Athlete'}</button>
                          <span className={`text-xs ${darkMode ? 'text-white/20' : 'text-gray-400'}`}>
                            {event.athlete?.school}
                          </span>
                        </div>
                        {/* Event Type Badge */}
                        <div className="flex items-center gap-2 mb-2">
                          {event.event_type === 'new_offer' && <Badge className="bg-[#16A34A]/15 text-[#16A34A] border-0 text-[10px]">🏈 New Offer</Badge>}
                          {event.event_type === 'committed' && <Badge className="bg-green-500/15 text-green-500 border-0 text-[10px]">🎉 Committed</Badge>}
                          {event.event_type === 'new_highlight' && <Badge className="bg-blue-500/15 text-blue-500 border-0 text-[10px]">🎬 New Highlight</Badge>}
                          {event.event_type === 'new_post' && (
                            <Badge className={`border-0 text-[10px] ${
                              event.metadata?.post_type === 'workout' ? 'bg-purple-500/15 text-purple-500' :
                              event.metadata?.post_type === 'achievement' ? 'bg-[#16A34A]/15 text-[#16A34A]' :
                              darkMode ? 'bg-white/10 text-white/40' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {event.metadata?.post_type === 'workout' ? '💪' : event.metadata?.post_type === 'achievement' ? '🏆' : '📝'} Post
                            </Badge>
                          )}
                          <span className={`text-[10px] ${darkMode ? 'text-white/15' : 'text-gray-300'}`}>
                            {new Date(event.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                        {/* Content */}
                        <p className={`text-sm leading-relaxed ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>
                          {event.description || event.title}
                        </p>
                        {/* Reactions */}
                        {event.metadata?.post_id && (
                          <div className="flex items-center gap-1 mt-2">
                            {['fire', 'clap', 'muscle', 'hundred'].map(r => {
                              const emoji = r === 'fire' ? '🔥' : r === 'clap' ? '👏' : r === 'muscle' ? '💪' : '💯';
                              const count = event.reactions?.[r] || 0;
                              const isActive = event.user_reacted === r;
                              return (
                                <button key={r} onClick={() => reactToPost(event.metadata.post_id, r)}
                                  className={`text-xs px-2 py-1 rounded-full transition-all ${
                                    isActive 
                                      ? 'bg-[#16A34A]/20 text-[#16A34A] font-bold' 
                                      : darkMode ? 'bg-white/5 text-white/30 hover:bg-white/10' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                                  }`}>
                                  {emoji} {count > 0 && count}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Inbox Tab */}
        {activeTab === 'inbox' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black">Messages</h2>
              {unreadCount > 0 && <Badge className="bg-red-500 text-white border-0">{unreadCount} unread</Badge>}
            </div>

            {/* Verification explainer */}
            <div className={`rounded-xl p-3 text-xs ${darkMode ? 'bg-white/[0.03] text-white/40' : 'bg-gray-50 text-gray-500'}`}>
              Messages arrive <span className="font-bold text-[#16A34A]">unverified</span>. If a message looks legit, click "Verify Sender" to generate a verification link. Share it with the sender — once they click it, the message is confirmed authentic.
            </div>

            {messages.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Mail className={`h-12 w-12 mx-auto mb-3 ${darkMode ? 'text-white/10' : 'text-gray-300'}`} />
                  <p className={darkMode ? 'text-white/40' : 'text-gray-500'}>No messages yet</p>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-white/20' : 'text-gray-400'}`}>
                    When coaches or others message you through your profile, they'll appear here.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {messages.map(msg => (
                  <Card key={msg.id} className={
                    msg.verified ? (darkMode ? 'border-green-500/30' : 'border-green-300') :
                    !msg.read ? (darkMode ? 'border-[#16A34A]/30 bg-[#16A34A]/5' : 'border-[#16A34A] bg-[#16A34A]/10') : ''
                  }>
                    <CardContent className="p-4">
                      {/* Header: name + badges */}
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-sm">{msg.sender_name}</p>
                          {msg.verified ? (
                            <Badge className="bg-green-500/15 text-green-500 border-green-500/25 text-[10px] font-bold">Verified ✓</Badge>
                          ) : (
                            <Badge className={`text-[10px] font-bold ${darkMode ? 'bg-white/5 text-white/30 border-white/10' : 'bg-gray-100 text-gray-400 border-gray-200'}`}>Unverified</Badge>
                          )}
                          {!msg.read && <div className="w-2 h-2 bg-[#16A34A] rounded-full" />}
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          {!msg.read && (
                            <Button variant="ghost" size="sm" onClick={() => markMessageRead(msg.id)}
                              className={darkMode ? 'text-white/30 hover:text-white/60' : 'text-gray-400'} title="Mark as read">
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => deleteMessage(msg.id)}
                            className="text-red-400 hover:text-red-600" title="Delete">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Sender details */}
                      <div className={`flex items-center gap-2 text-xs mb-2 flex-wrap ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>
                        <span>{msg.sender_email}</span>
                        {msg.sender_role && <><span>•</span><span>{msg.sender_role}</span></>}
                        {msg.sender_school && <><span>•</span><span>{msg.sender_school}</span></>}
                      </div>

                      {/* Message */}
                      <p className={`text-sm leading-relaxed ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>{msg.message}</p>
                      <p className={`text-xs mt-2 ${darkMode ? 'text-white/20' : 'text-gray-400'}`}>
                        {new Date(msg.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </p>

                      {/* Actions */}
                      <div className="mt-3 pt-3 border-t border-dashed flex items-center gap-3 flex-wrap" style={{borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}}>
                        {msg.verified ? (
                          <a href={`mailto:${msg.sender_email}?subject=Re: Message from your Offer profile`}
                            className="text-green-500 hover:text-green-400 text-xs font-semibold flex items-center gap-1">
                            <Mail className="h-3 w-3" /> Reply via Email
                          </a>
                        ) : !msg.verification_token ? (
                          <VerifySenderButton messageId={msg.id} userId={userId} darkMode={darkMode}
                            onVerificationSent={(token) => {
                              setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, verification_token: token } : m));
                            }} />
                        ) : (
                          <CopyVerifyLink token={msg.verification_token} darkMode={darkMode} />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            {/* Profile Header Card */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  {/* Photo */}
                  <div className="relative group">
                    <div className={`w-20 h-20 rounded-full overflow-hidden flex-shrink-0 ${darkMode ? 'bg-white/10' : 'bg-gray-100'}`}>
                      {profileData?.profile_picture_url ? (
                        <img src={profileData.profile_picture_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className={`w-full h-full flex items-center justify-center text-2xl font-bold ${darkMode ? 'text-white/30' : 'text-gray-300'}`}>
                          {profileData?.name?.charAt(0) || '?'}
                        </div>
                      )}
                    </div>
                    <label className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity">
                      <Upload className="h-5 w-5 text-white" />
                      <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
                    </label>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h2 className="text-xl font-black truncate">{profileData?.name}</h2>
                      <CheckCircle className="h-5 w-5 text-[#16A34A] flex-shrink-0" />
                    </div>
                    <p className={`text-sm ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>{profileData?.school} • Class of {profileData?.class_year}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {profileData?.positions?.map((pos, i) => (
                        <Badge key={i} className={`text-xs ${darkMode ? 'bg-[#16A34A]/15 text-[#16A34A] border-[#16A34A]/25' : 'bg-[#16A34A]/10 text-[#1a1a1a] border-[#16A34A]/30'}`}>{pos}</Badge>
                      ))}
                    </div>
                  </div>

                  <Button variant="outline" size="sm" onClick={() => profileData?.username && navigate(`/profile/${profileData.username}`)}
                    className={darkMode ? 'border-white/10 text-white/70 hover:bg-white/10' : ''} data-testid="view-public-profile-btn">
                    <Eye className="h-4 w-4 mr-1" /> View
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Profile Strength */}
            {(() => {
              const checklist = [
                { label: 'Profile photo', done: !!profileData?.profile_picture_url },
                { label: 'Positions', done: !!(editForm.positions?.length) },
                { label: 'GPA', done: !!editForm.gpa },
                { label: 'About / bio', done: !!editForm.about_me },
                { label: 'Head coach contact', done: !!(coachForm.name && coachForm.email) },
                { label: 'Measurables (HT/WT/40)', done: !!(measurables?.height || measurables?.weight || measurables?.forty_yard_dash) },
                { label: 'Highlight videos', done: (videos?.length || 0) > 0 },
                { label: 'An offer or interest', done: (offers?.length || 0) > 0 },
                { label: 'Socials (HUDL / X)', done: !!(editForm.hudl_url || editForm.x_url) },
              ];
              const done = checklist.filter(c => c.done).length;
              const pct = Math.round((done / checklist.length) * 100);
              return (
                <Card data-testid="profile-strength-card">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className={`font-bold text-sm ${darkMode ? 'text-white/80' : 'text-gray-800'}`}>Profile strength</h3>
                      <span className="text-[#16A34A] font-black text-sm">{pct}%</span>
                    </div>
                    <div className={`h-2 rounded-full overflow-hidden mb-4 ${darkMode ? 'bg-white/10' : 'bg-gray-100'}`}>
                      <div className="h-full bg-[#16A34A] transition-all duration-500" style={{ width: `${pct}%` }} />
                    </div>
                    <p className={`text-xs mb-3 ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>
                      {pct === 100 ? "🎉 Your profile is complete — coaches see the full picture!" : "Complete these to stand out to recruiters:"}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                      {checklist.map((c, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm" data-testid={`strength-item-${i}`}>
                          {c.done
                            ? <CheckCircle className="h-4 w-4 text-[#16A34A] flex-shrink-0" />
                            : <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 ${darkMode ? 'border-white/20' : 'border-gray-300'}`} />}
                          <span className={c.done ? (darkMode ? 'text-white/50 line-through' : 'text-gray-400 line-through') : (darkMode ? 'text-white/80' : 'text-gray-700')}>{c.label}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Profile Theme Picker */}
            <Card>
              <CardContent className="p-5">
                <h3 className={`font-bold text-sm mb-3 ${darkMode ? 'text-white/70' : 'text-gray-700'}`}>Public Profile Theme</h3>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'dark', label: 'Dark', bg: 'bg-black', accent: 'bg-[#16A34A]', ring: 'ring-[#16A34A]' },
                    { id: 'light', label: 'Light', bg: 'bg-white', accent: 'bg-[#16A34A]', ring: 'ring-[#16A34A]' },
                    { id: 'midnight', label: 'Midnight', bg: 'bg-slate-900', accent: 'bg-blue-500', ring: 'ring-blue-500' },
                  ].map(theme => (
                    <button
                      key={theme.id}
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/api/profile/${userId}`, { theme: theme.id });
                          setProfileData(p => ({ ...p, theme: theme.id }));
                          setEditForm(p => ({ ...p, theme: theme.id }));
                          toast.success(`Theme set to ${theme.label}!`);
                        } catch { toast.error('Failed to update theme'); }
                      }}
                      className={`relative rounded-xl overflow-hidden border-2 transition-all ${
                        (profileData?.theme || 'dark') === theme.id 
                          ? `${theme.ring} ring-2 ring-offset-2 ${darkMode ? 'ring-offset-black' : 'ring-offset-white'} border-transparent` 
                          : darkMode ? 'border-white/10 hover:border-white/20' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`${theme.bg} h-20 p-3 flex flex-col justify-between`}>
                        <div className="flex items-center gap-1.5">
                          <div className={`w-6 h-6 rounded-full ${theme.accent}`} />
                          <div className={`h-2 w-12 rounded ${theme.id === 'light' ? 'bg-gray-200' : 'bg-white/20'}`} />
                        </div>
                        <div className="flex gap-1">
                          <div className={`h-1.5 w-8 rounded ${theme.id === 'light' ? 'bg-gray-200' : 'bg-white/10'}`} />
                          <div className={`h-1.5 w-5 rounded ${theme.id === 'light' ? 'bg-gray-200' : 'bg-white/10'}`} />
                        </div>
                      </div>
                      <div className={`text-center py-1.5 text-xs font-semibold ${darkMode ? 'text-white/60' : 'text-gray-600'}`}>
                        {theme.label}
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Edit Profile Form — always visible, sectioned */}
            <Card data-testid="profile-edit-form">
              <CardHeader><CardTitle className="text-lg">Your Details</CardTitle></CardHeader>
              <CardContent className="space-y-6">
                {/* Basics */}
                <div>
                  <p className={`text-xs font-bold uppercase tracking-wider mb-3 ${darkMode ? 'text-white/40' : 'text-gray-400'}`}>Basics</p>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div><LabelHint darkMode={darkMode}>Full Name</LabelHint><Input value={editForm.name || ''} onChange={e => setEditForm(p => ({...p, name: e.target.value}))} data-testid="field-name" /></div>
                      <div><LabelHint darkMode={darkMode} hint="Your current high school.">School</LabelHint><Input value={editForm.school || ''} onChange={e => setEditForm(p => ({...p, school: e.target.value}))} data-testid="field-school" /></div>
                      <div><LabelHint darkMode={darkMode} hint="The year you graduate — coaches recruit by class year.">Class Year</LabelHint><Input value={editForm.class_year || ''} onChange={e => setEditForm(p => ({...p, class_year: e.target.value}))} placeholder="2027" data-testid="field-class_year" /></div>
                      <div><LabelHint darkMode={darkMode} hint="Your jersey number helps coaches spot you on film.">Jersey #</LabelHint><Input value={editForm.jersey_number || ''} onChange={e => setEditForm(p => ({...p, jersey_number: e.target.value}))} placeholder="7" data-testid="field-jersey" /></div>
                    </div>
                    <div><LabelHint darkMode={darkMode} hint="Pick every position you play — more positions = more opportunities.">Positions</LabelHint><PositionSelector selected={editForm.positions || []} onChange={positions => setEditForm(p => ({...p, positions}))} /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><LabelHint darkMode={darkMode} hint="Your hometown — coaches filter recruits by region.">City</LabelHint><Input value={editForm.city || ''} onChange={e => setEditForm(p => ({...p, city: e.target.value}))} placeholder="Dallas" data-testid="field-city" /></div>
                      <div><LabelHint darkMode={darkMode}>State</LabelHint><Input value={editForm.state || ''} onChange={e => setEditForm(p => ({...p, state: e.target.value}))} placeholder="TX" data-testid="field-state" /></div>
                    </div>
                    <div><LabelHint darkMode={darkMode} hint="A short motto shown at the top of your profile.">Tagline</LabelHint><Input value={editForm.tagline || ''} onChange={e => setEditForm(p => ({...p, tagline: e.target.value}))} placeholder="Blessed to compete every Friday night" maxLength={100} data-testid="field-tagline" /></div>
                    <div><LabelHint darkMode={darkMode} hint="Tell coaches your story, work ethic, and goals.">About Me</LabelHint><Textarea value={editForm.about_me || ''} onChange={e => setEditForm(p => ({...p, about_me: e.target.value}))} rows={3} data-testid="field-about" /></div>
                  </div>
                </div>

                {/* Academics */}
                <div>
                  <p className={`text-xs font-bold uppercase tracking-wider mb-3 ${darkMode ? 'text-white/40' : 'text-gray-400'}`}>Academics</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div><LabelHint darkMode={darkMode} hint="Grades matter for eligibility and scholarships — add your GPA.">GPA</LabelHint><Input value={editForm.gpa || ''} onChange={e => setEditForm(p => ({...p, gpa: e.target.value}))} placeholder="3.8" data-testid="field-gpa" /></div>
                    <div><LabelHint darkMode={darkMode} hint="Optional — add your SAT score if you've taken it.">SAT Score</LabelHint><Input value={editForm.sat_score || ''} onChange={e => setEditForm(p => ({...p, sat_score: e.target.value}))} placeholder="1200" data-testid="field-sat" /></div>
                  </div>
                </div>

                {/* Head Coach */}
                <div>
                  <p className={`text-xs font-bold uppercase tracking-wider mb-1 ${darkMode ? 'text-white/40' : 'text-gray-400'}`}>Head Coach</p>
                  <p className={`text-xs mb-3 ${darkMode ? 'text-white/30' : 'text-gray-400'}`}>College coaches often call your head coach first — make it easy for them.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div><LabelHint darkMode={darkMode} hint="Your head coach's full name.">Coach Name</LabelHint><Input value={coachForm.name} onChange={e => setCoachForm(p => ({...p, name: e.target.value}))} placeholder="Coach Smith" data-testid="field-coach-name" /></div>
                    <div><LabelHint darkMode={darkMode} hint="Required to save coach info.">Coach Email</LabelHint><Input value={coachForm.email} onChange={e => setCoachForm(p => ({...p, email: e.target.value}))} placeholder="coach@school.edu" data-testid="field-coach-email" /></div>
                    <div><LabelHint darkMode={darkMode}>Coach Phone</LabelHint><Input value={coachForm.phone} onChange={e => setCoachForm(p => ({...p, phone: e.target.value}))} placeholder="(555) 123-4567" data-testid="field-coach-phone" /></div>
                  </div>
                </div>

                {/* Links */}
                <div>
                  <p className={`text-xs font-bold uppercase tracking-wider mb-3 ${darkMode ? 'text-white/40' : 'text-gray-400'}`}>Links</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div><LabelHint darkMode={darkMode} hint="Your HUDL highlight link — the #1 thing coaches watch.">HUDL URL</LabelHint><Input value={editForm.hudl_url || ''} onChange={e => setEditForm(p => ({...p, hudl_url: e.target.value}))} data-testid="field-hudl" /></div>
                    <div><LabelHint darkMode={darkMode} hint="Where you post your offers and announcements.">Twitter / X URL</LabelHint><Input value={editForm.x_url || ''} onChange={e => setEditForm(p => ({...p, x_url: e.target.value}))} data-testid="field-x" /></div>
                    <div><LabelHint darkMode={darkMode}>Instagram URL</LabelHint><Input value={editForm.instagram_url || ''} onChange={e => setEditForm(p => ({...p, instagram_url: e.target.value}))} data-testid="field-ig" /></div>
                  </div>
                </div>

                {/* Custom Links */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className={`text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-white/40' : 'text-gray-400'}`}>Custom Links</p>
                    <Button type="button" size="sm" variant="outline" onClick={addCustomLink} data-testid="add-custom-link-btn"><Plus className="h-3 w-3 mr-1" /> Add Link</Button>
                  </div>
                  <p className={`text-xs mb-3 ${darkMode ? 'text-white/30' : 'text-gray-400'}`}>MaxPreps, recruiting portals, personal site, etc. Leave empty to hide.</p>
                  <div className="space-y-2">
                    {(editForm.custom_links || []).map(link => (
                      <div key={link.id} className="flex gap-2 items-center" data-testid={`custom-link-row-${link.id}`}>
                        <Input value={link.label || ''} onChange={e => updateCustomLink(link.id, 'label', e.target.value)} placeholder="Label (e.g. MaxPreps)" className="w-40" data-testid="custom-link-label" />
                        <Input value={link.url || ''} onChange={e => updateCustomLink(link.id, 'url', e.target.value)} placeholder="https://..." className="flex-1" data-testid="custom-link-url" />
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeCustomLink(link.id)} className="text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                    {(editForm.custom_links || []).length === 0 && <p className={`text-xs ${darkMode ? 'text-white/30' : 'text-gray-400'}`}>No custom links yet.</p>}
                  </div>
                </div>

                {/* Photos */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className={`text-xs font-bold uppercase tracking-wider ${darkMode ? 'text-white/40' : 'text-gray-400'}`}>Photos</p>
                    <label className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-md bg-[#0F5132] text-white cursor-pointer hover:bg-[#0B3B24]" data-testid="upload-photo-label">
                      {photoUploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Upload
                      <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={photoUploading} data-testid="photo-upload-input" />
                    </label>
                  </div>
                  <p className={`text-xs mb-3 ${darkMode ? 'text-white/30' : 'text-gray-400'}`}>Action shots, game photos, etc. Shown in the Photos tab on your profile.</p>
                  {photos.length === 0 ? (
                    <p className={`text-xs ${darkMode ? 'text-white/30' : 'text-gray-400'}`}>No photos yet.</p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {photos.map(ph => (
                        <div key={ph.id} className="relative group rounded-lg overflow-hidden aspect-square bg-gray-100" data-testid={`photo-${ph.id}`}>
                          <img src={`${API}${ph.url}`} alt="" className="w-full h-full object-cover" />
                          <button onClick={() => deletePhoto(ph.id)} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-photo-${ph.id}`}><Trash2 className="h-3 w-3" /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-2 pt-2">
                  <Button onClick={saveProfile} className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold" data-testid="save-profile-btn">
                    <Save className="h-4 w-4 mr-1" /> Save Changes
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Offers Tab */}
        {activeTab === 'offers' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black">My Offers</h2>
              <Button onClick={() => { setShowOfferForm(true); setEditingOffer(null); setOfferForm({ college_id: '', status: 'offer', notes: '', source_url: '' }); }} 
                className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold" size="sm">
                <Plus className="h-4 w-4 mr-1" /> Add Offer
              </Button>
            </div>

            {/* Offer Form */}
            {(showOfferForm || editingOffer) && (
              <Card>
                <CardContent className="p-4 space-y-3">
                  <div>
                    <Label>Search College</Label>
                    <Input 
                      value={collegeSearch} 
                      onChange={e => setCollegeSearch(e.target.value)} 
                      placeholder="Search colleges..."
                    />
                    {collegeSearch && filteredColleges.length > 0 && (
                      <div className="mt-1 max-h-40 overflow-y-auto border rounded-lg bg-white">
                        {filteredColleges.map(c => (
                          <button key={c.id || c.name} onClick={() => {
                            setOfferForm(p => ({...p, collegeId: c.id}));
                            setCollegeSearch(c.name);
                          }} className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2 text-sm">
                            {c.logoUrl && <img src={c.logoUrl} className="w-6 h-6 object-contain" alt="" />}
                            <span>{c.name}</span>
                            <span className="text-gray-400 text-xs ml-auto">{c.division}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>Status</Label>
                    <select value={offerForm.status} onChange={e => setOfferForm(p => ({...p, status: e.target.value}))}
                      className="w-full h-10 px-3 border rounded-md text-sm">
                      <option value="interested">Interested</option>
                      <option value="offer">Offer</option>
                      <option value="committed">Committed</option>
                      <option value="declined">Declined</option>
                    </select>
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Textarea value={offerForm.note || ''} onChange={e => setOfferForm(p => ({...p, note: e.target.value}))} rows={2} placeholder="Any details about this offer..." />
                  </div>
                  <div>
                    <Label>Offer Announcement — X (Twitter) Post <span className={darkMode ? 'text-white/30' : 'text-gray-400'}>(optional)</span></Label>
                    <Input value={offerForm.tweet_url || ''} onChange={e => setOfferForm(p => ({...p, tweet_url: e.target.value}))} 
                      placeholder="https://x.com/username/status/123..." data-testid="offer-tweet-url-input" />
                    <p className={`text-xs mt-1 ${darkMode ? 'text-white/20' : 'text-gray-400'}`}>
                      Paste the link to your offer announcement post — it embeds right on your public profile as proof.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={editingOffer ? updateOffer : addOffer} className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold" size="sm">
                      {editingOffer ? 'Update' : 'Add'} Offer
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => { setShowOfferForm(false); setEditingOffer(null); }}>Cancel</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Offers List */}
            {offers.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Trophy className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No offers yet. Add your first offer!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                  {offers.map(offer => (
                  <Card key={offer.id}>
                    <CardContent className="p-4 flex items-center gap-3">
                      {offer.college?.logoUrl ? (
                        <img src={offer.college.logoUrl} alt="" className="w-10 h-10 rounded object-contain bg-gray-50 p-1" />
                      ) : (
                        <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center"><School className="h-5 w-5 text-gray-300" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm truncate">{offer.college?.name || 'Unknown College'}</p>
                        <p className="text-gray-400 text-xs">{offer.college?.division} {offer.note && `• ${offer.note}`}</p>
                      </div>
                      <Badge className={`capitalize text-xs ${
                        offer.status === 'committed' ? 'bg-green-100 text-green-700' :
                        offer.status === 'offer' ? 'bg-[#16A34A]/15 text-[#1a1a1a]' :
                        offer.status === 'interested' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{offer.status}</Badge>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => {
                          setEditingOffer(offer);
                          setOfferForm({ collegeId: offer.collegeId, status: offer.status, note: offer.note || '', sourceUrl: offer.sourceUrl || '', tweet_url: offer.tweet_url || '' });
                          setCollegeSearch(offer.college?.name || '');
                        }}><Edit3 className="h-3 w-3" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteOffer(offer.id)} className="text-red-400 hover:text-red-600">
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Events Tab */}
        {activeTab === 'events' && (
          <div className="space-y-4" data-testid="events-tab">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black">My Events</h2>
              <Button onClick={() => { setShowEventForm(true); setEditingEvent(null); setEventForm({ camp_name: '', camp_date: '', location: '', status: 'registered', notes: '' }); }}
                className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold" size="sm" data-testid="add-event-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Event
              </Button>
            </div>
            <p className={`text-sm ${darkMode ? 'text-white/40' : 'text-gray-500'}`}>Camps, combines, showcases and visits — these show on your public profile's Events tab.</p>

            {/* Event Form */}
            {showEventForm && (
              <Card>
                <CardContent className="p-4 space-y-3">
                  <p className="font-bold text-sm">{editingEvent ? 'Edit event' : 'New event'}</p>
                  <div>
                    <Label>Event name *</Label>
                    <Input value={eventForm.camp_name} onChange={e => setEventForm(p => ({ ...p, camp_name: e.target.value }))}
                      placeholder="e.g. Ohio State Elite Camp" data-testid="event-name-input" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Date</Label>
                      <Input type="date" value={eventForm.camp_date} onChange={e => setEventForm(p => ({ ...p, camp_date: e.target.value }))}
                        data-testid="event-date-input" />
                    </div>
                    <div>
                      <Label>Status</Label>
                      <select value={eventForm.status} onChange={e => setEventForm(p => ({ ...p, status: e.target.value }))}
                        className="w-full h-10 px-3 border rounded-md text-sm" data-testid="event-status-select">
                        <option value="registered">Registered</option>
                        <option value="attended">Attended</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input value={eventForm.location} onChange={e => setEventForm(p => ({ ...p, location: e.target.value }))}
                      placeholder="Columbus, OH" data-testid="event-location-input" />
                  </div>
                  <div>
                    <Label>Notes <span className={darkMode ? 'text-white/30' : 'text-gray-400'}>(optional)</span></Label>
                    <Textarea value={eventForm.notes} onChange={e => setEventForm(p => ({ ...p, notes: e.target.value }))} rows={2}
                      placeholder="Any details about this event..." data-testid="event-notes-input" />
                  </div>
                  {profileData?.team_id && (
                    <button type="button" onClick={() => setEventForm(p => ({ ...p, share_to_team: !p.share_to_team }))} data-testid="event-share-team-toggle"
                      className={`flex items-center gap-2 text-sm font-bold px-3 py-2 rounded-full w-fit transition-all ${
                        eventForm.share_to_team ? 'bg-[#16A34A] text-white' : darkMode ? 'bg-white/5 text-white/50' : 'bg-gray-100 text-gray-500'
                      }`}>
                      <Users className="h-4 w-4" /> {eventForm.share_to_team ? 'Sharing to team page' : 'Share to team page'}
                    </button>
                  )}
                  <div className="flex gap-2">
                    <Button onClick={saveEvent} className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold" size="sm" data-testid="event-save-btn">{editingEvent ? 'Save Changes' : 'Add Event'}</Button>
                    <Button variant="outline" size="sm" onClick={() => { setShowEventForm(false); setEditingEvent(null); }}>Cancel</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Events List */}
            {camps.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No events yet. Add a camp, combine or showcase!</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2" data-testid="events-list">
                {camps.map(c => (
                  <Card key={c.id}>
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className="w-10 h-10 rounded bg-[#16A34A]/10 flex items-center justify-center flex-shrink-0">
                        <Calendar className="h-5 w-5 text-[#0F5132]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm truncate">{c.camp_name || 'Event'}</p>
                        <p className="text-gray-400 text-xs truncate">
                          {[c.location, c.camp_date].filter(Boolean).join(' • ') || '—'}
                        </p>
                      </div>
                      <Badge className="capitalize text-xs bg-gray-100 text-gray-600">{c.status || 'registered'}</Badge>
                      <Button variant="ghost" size="sm" onClick={() => openEditEvent(c)} className="text-gray-400 hover:text-[#0F5132]" data-testid={`event-edit-${c.id}`}>
                        <Edit3 className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteEvent(c.id)} className="text-red-400 hover:text-red-600" data-testid={`event-delete-${c.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Highlights Tab */}
        {activeTab === 'highlights' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black">My Highlights</h2>
              <Button onClick={() => setShowVideoForm(true)} className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold" size="sm">
                <Plus className="h-4 w-4 mr-1" /> Add Video
              </Button>
            </div>

            {showVideoForm && (
              <Card>
                <CardContent className="p-4 space-y-3">
                  <div>
                    <Label>Video Title</Label>
                    <Input value={videoForm.video_title} onChange={e => setVideoForm(p => ({...p, video_title: e.target.value}))} placeholder="e.g., Junior Season Highlights" />
                  </div>
                  <div>
                    <Label>HUDL Video URL</Label>
                    <Input value={videoForm.hudl_video_url} onChange={e => setVideoForm(p => ({...p, hudl_video_url: e.target.value}))} placeholder="https://hudl.com/video/..." />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea value={videoForm.video_description} onChange={e => setVideoForm(p => ({...p, video_description: e.target.value}))} rows={2} />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={addVideo} className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold" size="sm">Add Video</Button>
                    <Button variant="outline" size="sm" onClick={() => setShowVideoForm(false)}>Cancel</Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {videos.length === 0 && availableVideos.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Play className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No highlights yet. Add your first video!</p>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="space-y-2">
                  {videos.map(video => (
                    <Card key={video.id}>
                      <CardContent className="p-3 flex items-center gap-3">
                        <div className="w-24 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                          {video.og_image ? (
                            <img src={video.og_image} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center"><Play className="h-5 w-5 text-gray-300" /></div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate">{video.video_title || 'Untitled'}</p>
                          <p className="text-gray-400 text-xs truncate">{video.video_description || video.og_description || ''}</p>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => deleteVideo(video.id)} className="text-red-400 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Available Videos */}
                {availableVideos.length > 0 && (
                  <div>
                    <h3 className="font-bold text-sm text-gray-500 mb-2">Available Videos ({availableVideos.length})</h3>
                    <div className="space-y-2">
                      {availableVideos.map(video => (
                        <Card key={video.id} className="border-dashed">
                          <CardContent className="p-3 flex items-center gap-3">
                            <div className="w-20 h-14 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                              {video.og_image ? (
                                <img src={video.og_image} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center"><Play className="h-4 w-4 text-gray-300" /></div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{video.video_title || 'Untitled'}</p>
                            </div>
                            <Button size="sm" data-testid={`add-from-available-${video.id}`} onClick={() => addFromAvailable(video.id)} className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold">
                              <Plus className="h-3 w-3 mr-1" /> Add
                            </Button>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black">Season Stats</h2>
              {!showAddSeason ? (
                <Button onClick={() => setShowAddSeason(true)} className="bg-[#0F5132] hover:bg-[#0B3B24] text-white font-bold" size="sm">
                  <Plus className="h-4 w-4 mr-1" /> Add Season
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Input value={newSeasonName} onChange={e => setNewSeasonName(e.target.value)} placeholder="e.g., Junior Year"
                    className="w-40" onKeyPress={e => e.key === 'Enter' && addSeason()} />
                  <Button size="sm" onClick={addSeason} className="bg-[#0F5132] text-white font-bold">Add</Button>
                  <Button size="sm" variant="outline" onClick={() => setShowAddSeason(false)}>Cancel</Button>
                </div>
              )}
            </div>

            {stats.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Star className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No seasons added yet. Click "Add Season" to start tracking stats.</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Season Tabs */}
                <div className="flex flex-wrap gap-2">
                  {stats.map(s => (
                    <Button key={s.season} variant={selectedSeason === s.season ? 'default' : 'outline'} size="sm"
                      onClick={() => setSelectedSeason(s.season)}
                      className={selectedSeason === s.season ? 'bg-[#0F5132] text-white' : ''}>
                      {s.season}
                    </Button>
                  ))}
                </div>

                {selectedSeason && currentStats && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{selectedSeason}</CardTitle>
                        <Button variant="outline" size="sm" onClick={() => editingStats ? saveStats() : setEditingStats(true)}>
                          {editingStats ? <><Save className="h-3 w-3 mr-1" /> Save</> : <><Edit3 className="h-3 w-3 mr-1" /> Edit</>}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label>Games Played</Label>
                        {editingStats ? (
                          <Input type="number" value={currentStats.games_played || 0}
                            onChange={e => handleStatChange('root', 'games_played', parseInt(e.target.value) || 0)} />
                        ) : (
                          <p className="font-bold">{currentStats.games_played || 0}</p>
                        )}
                      </div>

                      {/* Offensive Stats */}
                      <div>
                        <h4 className="font-bold text-sm mb-2 text-[#16A34A]">Offensive Stats</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {['rush_attempts', 'rush_yards', 'rush_touchdowns', 'receptions', 'receiving_yards', 'receiving_touchdowns', 'pass_attempts', 'pass_completions', 'pass_yards', 'pass_touchdowns', 'interceptions_thrown', 'two_point_conversions', 'fumbles', 'fumbles_lost'].map(field => (
                            <div key={field}>
                              <Label className="text-xs capitalize">{field.replace(/_/g, ' ')}</Label>
                              {editingStats ? (
                                <Input type="number" value={currentStats.offensive_stats?.[field] || ''}
                                  onChange={e => handleStatChange('offensive_stats', field, parseInt(e.target.value) || 0)} className="h-8 text-sm" />
                              ) : (
                                <p className="text-sm font-medium">{currentStats.offensive_stats?.[field] || 0}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Defensive Stats */}
                      <div>
                        <h4 className="font-bold text-sm mb-2 text-[#16A34A]">Defensive Stats</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {['solo_tackles', 'assisted_tackles', 'tackles_for_loss', 'sacks', 'interceptions', 'forced_fumbles', 'fumble_recoveries', 'qb_hurries', 'pass_breakups'].map(field => (
                            <div key={field}>
                              <Label className="text-xs capitalize">{field.replace(/_/g, ' ')}</Label>
                              {editingStats ? (
                                <Input type="number" value={currentStats.defensive_stats?.[field] || ''}
                                  onChange={e => handleStatChange('defensive_stats', field, parseInt(e.target.value) || 0)} className="h-8 text-sm" />
                              ) : (
                                <p className="text-sm font-medium">{currentStats.defensive_stats?.[field] || 0}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Special Teams */}
                      <div>
                        <h4 className="font-bold text-sm mb-2 text-[#16A34A]">Special Teams</h4>
                        <div className="grid grid-cols-2 gap-2">
                          {['field_goals_made', 'field_goals_attempted', 'punt_attempts', 'punt_yards'].map(field => (
                            <div key={field}>
                              <Label className="text-xs capitalize">{field.replace(/_/g, ' ')}</Label>
                              {editingStats ? (
                                <Input type="number" value={currentStats.special_teams_stats?.[field] || ''}
                                  onChange={e => handleStatChange('special_teams_stats', field, parseInt(e.target.value) || 0)} className="h-8 text-sm" />
                              ) : (
                                <p className="text-sm font-medium">{currentStats.special_teams_stats?.[field] || 0}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </div>
        )}

        {/* Measurables Tab */}
        {activeTab === 'measurables' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black">Measurables</h2>
              <Button variant="outline" size="sm" onClick={() => editingMeasurables ? saveMeasurables() : setEditingMeasurables(true)}>
                {editingMeasurables ? <><Save className="h-3 w-3 mr-1" /> Save</> : <><Edit3 className="h-3 w-3 mr-1" /> Edit</>}
              </Button>
            </div>

            <Card>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {[
                    { key: 'height', label: 'Height', placeholder: "6'2\"" },
                    { key: 'weight', label: 'Weight (lbs)', placeholder: '210' },
                    { key: 'forty_yard_dash', label: '40-Yard Dash', placeholder: '4.6' },
                    { key: 'bench_press', label: 'Bench Press (lbs)', placeholder: '225' },
                    { key: 'squat', label: 'Squat (lbs)', placeholder: '405' },
                    { key: 'vertical_jump', label: 'Vertical Jump (in)', placeholder: '32' },
                    { key: 'broad_jump', label: 'Broad Jump', placeholder: "9'6\"" },
                    { key: 'shuttle', label: 'Shuttle', placeholder: '4.3' },
                    { key: 'power_clean', label: 'Power Clean (lbs)', placeholder: '275' },
                  ].map(item => (
                    <div key={item.key}>
                      <Label className="text-xs">{item.label}</Label>
                      {editingMeasurables ? (
                        <Input value={measurablesForm[item.key] || ''} 
                          onChange={e => setMeasurablesForm(p => ({...p, [item.key]: e.target.value}))} 
                          placeholder={item.placeholder}
                          className="h-9" />
                      ) : (
                        <p className="font-bold text-lg">{measurables[item.key] || '—'}</p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
